import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { getAccessToken } from '@/store/tokenAccess';
import { ApiError } from '@/types';

const createApiClient = (baseURL: string): AxiosInstance => {
  const client = axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json',
    },
    timeout: 30000,
  });

  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      // Читаем токен из tokenAccess (а не напрямую из authStore), чтобы избежать
      // цикла импортов api → authStore → authService → api, из-за которого
      // getAuthToken при ESM-инициализации привязывался к несозданному store
      // и interceptor всегда получал null → запросы уходили без Authorization.
      const token = getAccessToken();
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  let isRefreshing = false;
  let failedQueue: Array<{
    resolve: (token: string) => void;
    reject: (error: Error) => void;
  }> = [];

  const processQueue = (error: Error | null, token: string | null = null) => {
    failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else if (token) {
        prom.resolve(token);
      }
    });
    failedQueue = [];
  };

  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError<ApiError>) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      if (error.response?.status === 401 && !originalRequest._retry) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${token}`;
              }
              return client(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          // Динамический импорт authStore здесь (а не на верхнем уровне модуля),
          // чтобы не создавать цикл импортов api → authStore → authService → api.
          // refreshAuthToken/clearAuth живут в authStore и обращаются к authService,
          // что безопасно в рантайме — к этому моменту все модули уже инициализированы.
          // clearAuth намеренно не деструктурируем здесь: используется только в catch.
          const { refreshAuthToken } = await import('@/store/authStore');
          const newToken = await refreshAuthToken();
          processQueue(null, newToken);

          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
          }
          return client(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError as Error, null);
          const { clearAuth } = await import('@/store/authStore');
          clearAuth();
          window.location.href = '/auth/login';
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    }
  );

  return client;
};

export const authApi = createApiClient(import.meta.env.VITE_API_AUTH_URL || '/api/auth');
export const manageApi = createApiClient(import.meta.env.VITE_API_MANAGE_URL || '/api');
export const notificationApi = createApiClient(import.meta.env.VITE_API_NOTIFICATION_URL || '/api/notifications');

export const api = {
  auth: authApi,
  manage: manageApi,
  notification: notificationApi,
};

export default api;